import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Route, Routes, useNavigate } from "react-router-dom";
import {
  ArrowIcon,
  InfoFilledIcon,
  InfoIcon,
  LinkIcon,
  SuccessTickIcon,
  TickIconWithCircle,
  UploadIcon
} from "../../front-end-global-components/assets/assets";
import Button from "../../front-end-global-components/components/Button/Button";
import FeatureFlagsFallback from "../../front-end-global-components/components/FeatureFlagsFallback/FeatureFlagsFallback";
import Modal from "../../front-end-global-components/components/Modal/Modal";
import PatientTimeline from "../../front-end-global-components/components/PatientTimeline/PatientTimeline";
import {
  getRemainingTime,
  shareUrl
} from "../../front-end-global-components/utils/constants";
import { rippleEffect } from "../../front-end-global-components/utils/Functions";

import {
  getDocumentDownloadUrl,
  getProfilePictureDownloadUrl
} from "../../redux/downloads/actions";
import {
  generateNewLinkingToken,
  healthLockerAuthorization
} from "../../redux/patients/actions";
import { app, validDocumentType } from "../../utils/constants";
import { isValidObject } from "../../utils/validators";
import admissionActiveIcon from "../../front-end-global-components/assets/admission-active-icon.svg";
import admission from "../../front-end-global-components/assets/admission.svg";
import createPrescription from "../../front-end-global-components/assets/create_prescription.svg";
import formActiveIcon from "../../front-end-global-components/assets/form-active.svg";
import form from "../../front-end-global-components/assets/form.svg";
import insuranceActiveIcon from "../../front-end-global-components/assets/insurance-active-icon.svg";
import insurance from "../../front-end-global-components/assets/insurance.svg";
import prescriptionActive from "../../front-end-global-components/assets/prescription-active-icon.svg";
import prescription from "../../front-end-global-components/assets/prescription.svg";
import receiptActiveIcon from "../../front-end-global-components/assets/receipt-active-icon.svg";
import receipt from "../../front-end-global-components/assets/receipt.svg";
import reportActive from "../../front-end-global-components/assets/report-active-icon.svg";
import report from "../../front-end-global-components/assets/report.svg";
import symptomActiveIcon from "../../front-end-global-components/assets/symptom-active-icon.svg";
import symptom from "../../front-end-global-components/assets/symptom.svg";
import consultationIcon from "../../front-end-global-components/assets/consultation.svg";
import uploadPrescription from "../../front-end-global-components/assets/upload_prescription.svg";
import vaccineActiveIcon from "../../front-end-global-components/assets/vaccineActiveIcon.svg";
import vaccineIcon from "../../front-end-global-components/assets/vaccineIcon.svg";
import vitalActiveIcon from "../../front-end-global-components/assets/vital-active-icon.svg";
import vital from "../../front-end-global-components/assets/vital.svg";
import {
  addDocumentFilter,
  removeDocumentFilter,
  retryDataTransfer
} from "../../redux/documents/actions";
import { careContexts } from "../../services/database";
import { setErrorStatus } from "../../redux/status/actions";

function Timeline(props) {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [showRetryButton, setShowRetryButton] = useState(false);
  const [isDataTransferRequested, setIsDataTransferRequested] = useState(false);

  useEffect(() => {
    const currentProfile = props.patients?.currentProfile;

    if (!props.documents?.dataTransfers || !currentProfile) {
      setIsDataTransferRequested(false);
      setShowRetryButton(false);
      return;
    }

    const dataTransfers = Object.values(props.documents.dataTransfers).filter(
      (d) => d.patient.patientId === currentProfile
    );

    if (dataTransfers.length === 0) {
      setIsDataTransferRequested(false);
      setShowRetryButton(false);
      return;
    }

    const hasRequested = dataTransfers.some((d) => d.status === "REQUESTED");
    setIsDataTransferRequested(hasRequested);

    const FIVE_MIN = 5 * 60 * 1000;
    const allExpired = dataTransfers.every(
      (d) => Date.now() - d.timestamp > FIVE_MIN
    );

    setShowRetryButton(allExpired);
  }, [props.documents?.dataTransfers, props.patients?.currentProfile]);

  return (
    <>
      <PatientTimeline
        getMenuItems={getMenuItems}
        documentFilterMenu={[
          "prescription",
          "report",
          "admission",
          "vital",
          "vaccine",
          "consultation",
          "invoice",
          "others"
        ]}
        hideShare={true}
        kycStatus={
          props.patients?.healthIdData?.[props.patients?.currentProfile]
            ?.kycStatus
        }
        kycStatusClick={() => navigate("/settings/abhaNumber")}
        removeScrollBar={true}
        hideUploadButton={false}
        loading={props.documents?.loading || props.patients?.loading}
        downloads={props.downloads?.data}
        profilePictures={props.downloads?.data?.profilePictures}
        documents={
          isValidObject(props.documents?.data?.[props.patients?.currentProfile])
            ? Object.values(
                props.documents?.data?.[props.patients?.currentProfile]
              ).sort((a, b) => b.uploadedAt.timestamp - a.uploadedAt.timestamp)
            : []
        }
        transferStatus={isDataTransferRequested}
        dataTransfer={
          props.documents?.dataTransfers
            ? Object.values(props.documents.dataTransfers).filter(
                (data) =>
                  data.patient.patientId === props.patients?.currentProfile
              )
            : []
        }
        documentCardOnClick={(data) => {
          if (["fitness"].includes(data.documentType)) return;
          if (data.consentId) {
            retryDataTransfer?.({ consentId: data.consentId });
          } else {
            navigate(
              `/patientTimeline/${validDocumentType(data.documentType)}/${
                data.documentId
              }`
            );
          }
        }}
        retryButtonOnClick={() => {
          console.log("Retrying data transfer for all documents");
          retryDataTransfer?.();
        }}
        showRetryButton={
          showRetryButton ||
          Boolean(!isValidObject(props.documents?.dataTransfer))
        }
        getAudioUrl={getDocumentDownloadUrl}
        getDocumentsCardProfilePicture={(userType, userId) =>
          getProfilePictureDownloadUrl(userType, userId)
        }
        allowedUploadDocuments={[
          "prescription",
          "report",
          "vital",
          "admission",
          "vaccine",
          "consultation",
          "invoice",
          "others"
        ]}
        viewerId={props.patients?.currentProfile}
        patientName={
          props.patients?.profiles?.[props.patients?.currentProfile]?.name
        }
        viewerType="patient"
        showExpiringSoon={false}
        showExpired={false}
        resubscribeOnClick={() => healthLockerAuthorization()}
        resubscribeLoading={props.patients?.loading}
        filterState={props.documents?.filters}
        onFilterClick={addDocumentFilter}
        onClearFilterClick={() => removeDocumentFilter?.("doctorAndClinic")}
        patient={props.patients?.profiles?.[props.patients?.currentProfile]}
        documentShare={props.documents.documentShare}
      />

      <Routes>
        <Route
          path="upload"
          element={
            <UploadMenuModal
              featureFlags={props.app?.featureFlags}
              menu={getMenuItems(props.documents?.filters)}
              isLoading={props.documents?.loading || props.patients?.loading}
              allowedUploadDocuments={[
                "prescription",
                "report",
                "vital",
                "admission",
                "vaccine",
                "consultation",
                "invoice",
                "others"
              ]}
              onNewDocumentCreate={props.onNewDocumentCreate}
            />
          }
        />
        <Route
          path="options"
          element={
            <OptionsModal
              linkHealthRecords={() => navigate("/clinicSearch")}
              loading={props.patients?.loading || loading}
              uploadDocumentClick={async () => {
                setLoading(true);

                try {
                  const profileId = props.patients?.currentProfile;

                  const linkingToken =
                    props.patients?.linkingToken?.[profileId];

                  const careContextList = await careContexts(profileId);

                  const isTokenValid =
                    linkingToken?.accessToken &&
                    linkingToken?.expiresAt?.timestamp > Date.now();

                  const isAnyCareContextValid =
                    isValidObject(careContextList) &&
                    Object.values(careContextList).some(
                      (care) => care?.expiresAt?.timestamp > Date.now()
                    );

                  if (isTokenValid || isAnyCareContextValid) {
                    navigate("/patientTimeline/upload");
                    return;
                  }

                  generateNewLinkingToken(navigate);
                } catch (error) {
                  setErrorStatus(error);
                  console.log("uploadDocumentClick error:", error);
                } finally {
                  setLoading(false);
                }
              }}
            />
          }
        />
        <Route
          path="share"
          element={
            <ShareModal
              patient={
                props.patients?.profiles?.[props.patients?.currentProfile]
              }
              loading={props.patients?.loading}
              documentShare={props.documents.documentShare}
              expiresIn={getRemainingTime(
                props.documents.documentShare?.expiresAt
              )}
              showFallback={
                !props.documents?.data ||
                Object.keys(
                  props.documents?.data?.[props.patients?.currentProfile] ?? {}
                ).length === 0
              }
              appConfig={app}
            />
          }
        />
      </Routes>
    </>
  );
}

function OptionsModal(props) {
  const navigate = useNavigate();
  const addDocumentMenu = [
    {
      name: "Link health records from provider",
      description: "Fetch and link records to health locker",
      onClick: props.linkHealthRecords,
      "data-cy": "SHARE-HEALTH-ID",
      icon: <LinkIcon />,
      key: "link"
    },
    {
      name: "Upload health records",
      description: "Upload records to health locker",
      onClick: props.uploadDocumentClick,
      "data-cy": "UPLOAD-HEALTH-RECORDS",
      icon: <UploadIcon />,
      key: "upload"
    }
  ];

  return (
    <Modal
      show={true}
      canIgnore={true}
      onClose={() => navigate("/")}
      position="position-fixed bottom-0"
      boxShadow="none"
      borderRadius="none"
      width="inherit-parent-width"
      background="background-transparent"
      height="height-fit-to-content"
    >
      <div className="background-white padding-default font-family-gilroy-medium font-color-secondary flex-align-items-center font-size-medium padding-large flex-wrap patient-timeline-upload-icon-gap">
        <div className="font-size-small font-color-secondary text-align-center padding-vertical-large">
          UPLOAD / LINK DOCUMENTS
        </div>
        {addDocumentMenu.map((data, index) => (
          <div
            key={index}
            className="flex-justify-content-space-between flex-align-items-center padding-vertical-large"
            data-cy={data["data-cy"]}
            onClick={data.onClick}
          >
            <div className="display-flex font-color-secondary">
              <div>
                <div className="display-flex flex-align-items-center">
                  {data.icon}
                  <div className="margin-left-default font-size-medium font-family-gilroy-regular font-size-large">
                    {data.name}
                  </div>
                </div>
                <div className="font-size-small font-family-gilroy-regular text-transform-capitalize padding-top-small margin-left-larger">
                  {data.description}
                </div>
              </div>
            </div>
            <div>
              {props.loading && data.key === "upload" ? (
                <div className="loading-spinner" data-cy="LOADING-SPINNER" />
              ) : (
                <ArrowIcon />
              )}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function UploadMenuModal(props) {
  const navigate = useNavigate();
  const allowedMenu = Array.isArray(props.allowedUploadDocuments)
    ? props.menu.filter((element) =>
        props.allowedUploadDocuments.includes(element.type)
      )
    : [];

  return (
    <Modal
      canIgnore={true}
      onClose={() => navigate("/")}
      show={true}
      position="position-fixed bottom-0"
      boxShadow="none"
      borderRadius="none"
      width="inherit-parent-width"
      background="background-transparent"
      height="height-fit-to-content"
    >
      <div className="background-white padding-default font-family-gilroy-medium font-color-secondary font-size-medium padding-larger">
        {props.isLoading && (
          <>
            <div className="display-flex flex-justify-content-center flex-align-items-center padding-bottom-large">
              <div
                style={{ width: "220px" }}
                className="shimmer padding-small text-align-center"
              />
            </div>
            <div className="flex-justify-content-space-around flex-align-items-center flex-wrap patient-timeline-upload-icon-gap">
              {allowedMenu.map((_element, index) => (
                <div
                  key={index}
                  className="padding-top-default padding-left-larger padding-right-larger"
                >
                  <div
                    style={{ borderRadius: "8px" }}
                    className="shimmer padding-larger"
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {!props.isLoading && (
          <>
            {!props.featureFlags?.uploadDocument && <FeatureFlagsFallback />}
            {props.featureFlags?.uploadDocument && (
              <>
                <div className="text-align-center padding-bottom-larger">
                  SELECT THE DOCUMENT TYPE
                </div>
                <div className="flex-justify-content-space-around flex-align-items-center flex-wrap patient-timeline-upload-icon-gap">
                  {allowedMenu.map((element) => (
                    <div
                      key={element.type}
                      onClick={(event) => {
                        rippleEffect(event);
                        props.onNewDocumentCreate?.(element.type);
                        navigate("/");
                      }}
                      className="text-align-center width-30-percentage cursor-pointer"
                      data-cy={element.dataCy}
                    >
                      <img
                        src={element.defaultIcon}
                        alt={element.alt}
                        className="home-add-documents-icon-size"
                      />
                      <div className="text-transform-capitalize white-space-preline">
                        {element.text}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function ShareModal(props) {
  const navigate = useNavigate();
  const [selectedExpiryHours, setSelectedExpiryHours] = useState(1);
  const expiryOptions = [1, 2, 24];

  const ShareSection = () => (
    <>
      <div className="font-size-smaller font-family-gilroy-regular margin-top-large margin-bottom-default">
        ACCESS EXPIRES IN
      </div>

      <div className="display-flex flex-align-items-center flex-justify-content-space-between margin-bottom-large">
        {expiryOptions.map((option) => (
          <div
            key={option}
            className="display-flex border-radius-default flex-align-items-center"
            style={{
              border:
                selectedExpiryHours === option
                  ? "1px solid var(--primaryColor)"
                  : "1px solid #E5E5E5",
              padding: "6px 24px",
              cursor: "pointer"
            }}
            onClick={() => setSelectedExpiryHours(option)}
          >
            {selectedExpiryHours === option && (
              <div className="margin-right-small flex-start-place-children-vertically">
                <TickIconWithCircle />
              </div>
            )}
            <div
              className={`font-size-small font-family-gilroy-regular ${
                selectedExpiryHours !== option
                  ? "margin-left-large"
                  : "font-color-primary"
              }`}
            >
              {option} {option === 1 ? "Hour" : "Hours"}
            </div>
          </div>
        ))}
      </div>

      <div className="display-flex margin-top-larger">
        <InfoIcon width="20" height="20" />
        <div className="font-size-small margin-left-default font-family-gilroy-regular">
          By approving, you agree with{" "}
          <span className="font-family-gilroy-medium">Ninto's</span> terms of
          service & privacy policies. You can stop access any time.
        </div>
      </div>

      <div
        className="display-flex flex-justify-content-space-between margin-top-large"
        style={{ gap: "16px" }}
      >
        <Button
          boxShadow={false}
          data-cy="SHARE-TIMELINE-BUTTON"
          text="Share via"
          loading={props.loading}
          onClick={() => {
            props.createDocumentShare({
              patient: {
                id: props.patient.documentId,
                healthId: props.patient.healthId,
                name: props.patient.name,
                phoneNumber: props.patient.phoneNumber
              },
              expiresAt: Date.now() + selectedExpiryHours * 60 * 60 * 1000,
              createdAt: Date.now(),
              revoked: false,
              baseUrl: props.appConfig.guests.baseUrl
            });
          }}
        />
        <Button
          boxShadow={false}
          data-cy="CANCEL-BUTTON"
          text="Cancel"
          variant="secondary"
          onClick={() => navigate("/patientTimeline")}
        />
      </div>
    </>
  );

  const ActiveSection = () => (
    <>
      <div className="inherit-parent-width flex-justify-content-center font-family-gilroy-regular">
        <div className="text-align-center">
          <SuccessTickIcon />
          <div className="font-family-gilroy-regular margin-top-default">
            Currently sharing your health records
          </div>
        </div>
      </div>

      <div className="display-flex flex-justify-content-space-between flex-align-items-center padding-medium border-1px-e5e5e5 border-radius-default margin-top-larger">
        <div>
          <div className="font-size-smaller font-family-gilroy-regular">
            ACCESS EXPIRES IN
          </div>
          <div className="margin-top-default">{props.expiresIn}</div>
        </div>
        <div
          className="font-size-small font-family-gilroy-regular padding-default flex-justify-content-center flex-align-items-center font-color-primary border-radius-default"
          style={{ background: "#00A0001F", padding: "4px 8px" }}
        >
          Active
        </div>
      </div>

      <div
        className="display-flex flex-justify-content-space-between margin-top-large"
        style={{ gap: "16px" }}
      >
        <Button
          boxShadow={false}
          data-cy="SHARE-BUTTON"
          text="Share via"
          onClick={() => {
            shareUrl({
              title: "Medical Timeline Shared",
              text: `Medical timeline of ${props.documentShare.patient.name}. Tap to view securely.`,
              url: props.documentShare?.baseUrl
                ? `${props.documentShare.baseUrl}/sharedTimeline?shareId=${props.documentShare.documentId}`
                : `${props.appConfig?.guests.baseUrl}/sharedTimeline?shareId=${props.documentShare.documentId}`
            });
          }}
        />
        <Button
          boxShadow={false}
          data-cy="REVOKE-BUTTON"
          text={<div className="font-color-red">Stop access</div>}
          variant="secondary"
          onClick={() =>
            props.updateDocumentShare(
              props.documentShare.documentId,
              { revoked: true },
              navigate
            )
          }
        />
      </div>
    </>
  );

  const FallbackSection = () => (
    <div className="text-align-center">
      <InfoFilledIcon />
      <div className="font-family-gilroy-medium font-size-large margin-bottom-small margin-top-large">
        No documents to share
      </div>
      <div className="font-family-gilroy-regular font-size-small font-color-muted margin-top-default">
        Once documents are added by yourself or your hospital, you'll be able to
        share the documents
      </div>
      <Button
        boxShadow={false}
        data-cy="done-button"
        text="Done"
        className="margin-top-larger font-weight-normal"
        onClick={() => navigate("..")}
      />
    </div>
  );

  return (
    <Modal
      show={true}
      canIgnore={true}
      onClose={() => navigate("/")}
      position="position-fixed bottom-0"
      boxShadow="none"
      borderRadius="none"
      width="inherit-parent-width"
      background="background-transparent"
      height="height-fit-to-content"
    >
      <div className="background-white padding-larger font-family-gilroy-medium font-color-secondary">
        {(props.documentShare?.revoked === false &&
          props.documentShare?.expiresAt > +new Date()) ||
        props.showFallback ? (
          <></>
        ) : (
          <div className="font-family-gilroy-regular font-size-small text-align-center">
            SHARE RECORDS
          </div>
        )}

        {props.showFallback ? (
          <FallbackSection />
        ) : props.documentShare?.revoked === false &&
          props.documentShare?.expiresAt > Date.now() ? (
          <ActiveSection />
        ) : (
          <ShareSection />
        )}
      </div>
    </Modal>
  );
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  documents: state.documents,
  patients: state.patients,
  downloads: state.downloads,
  app: state.app
});

export default connect(mapStateToProps)(Timeline);

const getMenuItems = (filterState) => {
  const items = [
    {
      type: "symptom",
      text: "Symptom",
      filterMenuText: "Symptoms",
      defaultIcon: symptom,
      activeIcon: symptomActiveIcon,
      dataCy: "upload-symptom-button",
      widthHeight: "filter-icon-height-and-width"
    },
    {
      type: "prescription",
      text: "Prescription",
      filterMenuText: "Prescriptions",
      defaultIcon: prescription,
      activeIcon: prescriptionActive,
      dataCy: "UPLOAD-PRESCRIPTION-BUTTON",
      widthHeight: "filter-icon-height-and-width"
    },
    {
      type: "report",
      text: "Report",
      filterMenuText: "Reports",
      defaultIcon: report,
      activeIcon: reportActive,
      dataCy: "upload-report-button",
      widthHeight: "filter-icon-height-and-width"
    },
    {
      type: "receipt",
      text: "Receipt",
      filterMenuText: "Receipts",
      defaultIcon: receipt,
      activeIcon: receiptActiveIcon,
      dataCy: "upload-receipt-button",
      widthHeight: "filter-icon-height-and-width"
    },
    {
      type: "admission",
      text: "Admission",
      filterMenuText: "Admissions",
      defaultIcon: admission,
      activeIcon: admissionActiveIcon,
      dataCy: "upload-admission-button",
      widthHeight: "filter-icon-height-and-width"
    },
    {
      type: "insurance",
      text: "Insurance",
      filterMenuText: "Insurances",
      defaultIcon: insurance,
      activeIcon: insuranceActiveIcon,
      dataCy: "upload-insurance-button",
      widthHeight: "filter-icon-height-and-width"
    },
    {
      type: "uploadPrescription",
      text: "Upload Prescription",
      filterMenuText: "",
      defaultIcon: uploadPrescription,
      activeIcon: uploadPrescription,
      dataCy: "prescription-upload-button",
      widthHeight: "home-add-documents-icon-size"
    },
    {
      type: "createPrescription",
      text: "Create Prescription",
      filterMenuText: "",
      defaultIcon: createPrescription,
      activeIcon: createPrescription,
      dataCy: "create-prescription-button",
      widthHeight: "home-add-documents-icon-size"
    },
    {
      type: "vital",
      text: "Vital",
      filterMenuText: "Vitals",
      defaultIcon: vital,
      activeIcon: vitalActiveIcon,
      dataCy: "UPLOAD-VITAL-BUTTON",
      widthHeight: "filter-icon-height-and-width"
    },
    {
      type: "form",
      text: "Forms",
      filterMenuText: "Forms",
      defaultIcon: form,
      activeIcon: formActiveIcon,
      dataCy: "upload-form-button",
      widthHeight: "filter-icon-height-and-width"
    },
    {
      type: "vaccine",
      text: "Vaccine",
      filterMenuText: "Vaccines",
      defaultIcon: vaccineIcon,
      activeIcon: vaccineActiveIcon,
      dataCy: "UPLOAD-VACCINE-BUTTON",
      widthHeight: "filter-icon-height-and-width"
    },
    {
      type: "consultation",
      text: "Consultation",
      filterMenuText: "Consultations",
      defaultIcon: consultationIcon,
      activeIcon: consultationIcon,
      dataCy: "upload-consultation-button",
      widthHeight: "filter-icon-height-and-width"
    },
    {
      type: "invoice",
      text: "Invoice",
      filterMenuText: "Invoices",
      defaultIcon: receipt,
      activeIcon: receiptActiveIcon,
      dataCy: "upload-invoice-button",
      widthHeight: "filter-icon-height-and-width"
    },
    {
      type: "others",
      text: "Other",
      filterMenuText: "Others",
      defaultIcon: receipt,
      activeIcon: receiptActiveIcon,
      dataCy: "upload-others-button",
      widthHeight: "filter-icon-height-and-width"
    }
  ];

  // Map over items to add dynamic icon & alt
  return items.map((item) => {
    const isActive = filterState.documentType.includes(item.type);
    return {
      ...item,
      icon: isActive ? item.activeIcon : item.defaultIcon,
      alt: `${isActive ? "active-" : ""}${item.type}-icon`
    };
  });
};
